import { Cell, NonEditableCell } from '@silevis/reactgrid';
import { useTheme } from '@mui/material';
import addCell from '../helpers/addCell';
import '@mui/material/styles';
import '@mui/icons-material';
import usePlanStore, {
  WeeklyWeight,
} from '../../../../../../../../../../../store/planStore';
import { Dataset } from '../interfaces';
import IconCell from '../templates/IconCell';
import getPercentFromValue from '../helpers/getPercentFromValue';
import normalizeByNumberOfDays from '../helpers/normalizeByNumberOfDays';

const useAddTotalsRow = (cells: Cell[]) => {
  const { countryForms, selectedCountry } = usePlanStore();
  const theme = useTheme();

  const stableRows = 3;
  const stableColumns = 2;
  const daysInFullWeekAmount = 7;

  const dataset: Dataset | undefined =
    countryForms[selectedCountry!]?.offline?.tv;

  const weeklyWeight: WeeklyWeight | undefined = dataset?.weeklyWeight;

  const weeks = dataset?.grpTable?.weeks ?? [];

  let nextWeek = weeks[1]?.weekNumber;
  let lastWeek: number | null = null;
  const rowIndex = (weeks[0]?.copy.value.length ?? 0) + stableRows;
  let colIndex = 2;

  addCell({
    cells,
    rowIndex: (weeks[0]?.copy.value.length ?? 0) + stableRows,
    colIndex: 0,
    Template: NonEditableCell,
    value: 'Total',
    sx: {
      color: theme.palette.grpTable.text.main,
      fontWeight: 700,
      fontSize: '15px',
      background: theme.palette.grpTable.background.totals,
    },
  });

  addCell({
    cells,
    rowIndex: (weeks[0]?.copy.value.length ?? 0) + stableRows,
    colIndex: 1,
    Template: NonEditableCell,
    sx: {
      background: theme.palette.grpTable.background.totals,
    },
  });

  weeks.forEach((week, index) => {
    nextWeek = weeks[index + 1]?.weekNumber;
    lastWeek = weeks[index - 1]?.weekNumber;
    colIndex = index + stableColumns;

    const value = dataset?.grpTable?.columnTotals?.[index] ?? 0;

    const getExceedingBoundaries = () => {
      let totalNumberOfDays = week.numberOfDays;

      if (nextWeek === week.weekNumber) {
        totalNumberOfDays += weeks[index + 1].numberOfDays;
      }

      return (
        (dataset?.grpTable?.columnTotals?.[index] ?? 0) >
          ((weeklyWeight?.max ?? 0) / daysInFullWeekAmount) *
            totalNumberOfDays ||
        (dataset?.grpTable?.columnTotals?.[index] ?? 0) <
          ((weeklyWeight?.min ?? 0) / daysInFullWeekAmount) * totalNumberOfDays
      );
    };

    const getExceedingWeeklyWeight = () => {
      let totalNumberOfDays = week.numberOfDays;

      if (weeks[index + 1]?.weekNumber === week.weekNumber) {
        totalNumberOfDays += weeks[index + 1].numberOfDays;
      }

      return (
        (dataset?.grpTable?.columnTotals?.[index] ?? 0) !==
        Math.round(
          ((weeklyWeight?.current ?? 0) / daysInFullWeekAmount) *
            totalNumberOfDays
        )
      );
    };

    const getWeekNumberOfDays = () => {
      if (lastWeek === week.weekNumber)
        return week.numberOfDays + weeks[index - 1].numberOfDays;
      if (nextWeek === week.weekNumber)
        return week.numberOfDays + weeks[index + 1].numberOfDays;
      return week.numberOfDays;
    };

    addCell({
      cells,
      rowIndex,
      colIndex,
      Template: IconCell,
      value: getPercentFromValue(
        value,
        normalizeByNumberOfDays(weeklyWeight?.current, getWeekNumberOfDays())
      ),
      colSpan: week?.weekNumber === nextWeek ? 2 : 1,
      isExceedingBoundaries: getExceedingBoundaries(),
      isExceedingWeeklyWeight: getExceedingWeeklyWeight(),
      sx: {
        color: theme.palette.grpTable.text.light,
        fontWeight: 700,
        fontSize: '14px',
        background: theme.palette.grpTable.background.totals,
      },
    });
  });
};

export default useAddTotalsRow;
